<template>
  <div class="change-order-page">
    <list-page-shell>
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openNew">{{ $t('pipelineList.newChangeOrder') }}</el-button>
      </template>

      <p v-if="filterContractId" class="filter-hint">{{ $t('changeOrderPage.filterHint', { id: filterContractId }) }}</p>

      <template #filters>
        <el-form :inline="true" :model="filters" size="small">
          <el-form-item :label="$t('changeOrderPage.contractFilter')">
            <el-select
              v-model="filters.contractId"
              filterable
              clearable
              :placeholder="$t('changeOrderPage.contractPh')"
              style="width:280px;"
              :loading="contractsLoading"
            >
              <el-option
                v-for="c in contractOptions"
                :key="c.id"
                :label="contractOptionLabel(c)"
                :value="c.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('changeOrderPage.changeNoLabel')">
            <el-input
              v-model="filters.changeNoLike"
              clearable
              :placeholder="$t('changeOrderPage.changeNoPh')"
              style="width:160px;"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item :label="$t('changeOrderPage.approvalFilter')">
            <el-select v-model="filters.approvalStatusCode" clearable :placeholder="$t('common.pleaseSelect')" style="width:140px;">
              <el-option v-for="o in approvalOptions" :key="o.value" :value="o.value" :label="o.label" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">{{ $t('common.search') }}</el-button>
            <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">{{ $t('common.reset') }}</el-button>
          </el-form-item>
        </el-form>
      </template>

      <el-table v-loading="loading" :data="rows" border stripe :empty-text="$t('common.empty')" style="width:100%;">
        <el-table-column prop="changeNo" :label="$t('grid.number')" width="140" />
        <el-table-column prop="contractId" :label="$t('grid.contractId')" width="88" />
        <el-table-column prop="amountDelta" :label="$t('grid.amountDelta')" width="120" align="right" />
        <el-table-column prop="descriptionText" :label="$t('grid.description')" min-width="160" show-overflow-tooltip />
        <el-table-column :label="$t('grid.approval')" width="100">
          <template slot-scope="s">{{ labelChangeOrderApproval(s.row.approvalStatusCode) }}</template>
        </el-table-column>
        <el-table-column :label="$t('grid.createdAt')" width="200">
          <template slot-scope="s">{{ formatDisplayDateTime(s.row.createdAt) || '—' }}</template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="200" fixed="right">
          <template slot-scope="s">
            <el-button v-if="canOfficeAct && changeOrderCanApproveReject(s.row.approvalStatusCode)" type="text" size="small" @click="approve(s.row)">{{ $t('publicQuote.approve') }}</el-button>
            <el-button v-if="canOfficeAct && changeOrderCanApproveReject(s.row.approvalStatusCode)" type="text" size="small" class="danger" @click="reject(s.row)">{{ $t('publicQuote.reject') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="list-pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :page-sizes="[10, 20, 50]"
          :total="total"
          :page-size="pageSize"
          :current-page="pageNo"
          @size-change="onSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </list-page-shell>

    <form-dialog :title="$t('changeOrderPage.newDialog')" :visible.sync="dlg" width="520px" @closed="resetForm">
      <el-form ref="coForm" :model="form" label-width="100px" size="small">
        <el-form-item :label="$t('changeOrderPage.linkContract')" required>
          <el-select
            v-model="form.contractId"
            filterable
            clearable
            :placeholder="$t('changeOrderPage.contractPh')"
            style="width:100%;"
            :loading="contractsLoading"
          >
            <el-option
              v-for="c in contractOptions"
              :key="c.id"
              :label="contractOptionLabel(c)"
              :value="c.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('grid.amountDelta')">
          <el-input v-model="form.amountDelta" :placeholder="$t('changeOrderPage.amountPh')" clearable />
        </el-form-item>
        <el-form-item :label="$t('changeOrderPage.descLabel')">
          <el-input v-model="form.descriptionText" type="textarea" :rows="3" :placeholder="$t('changeOrderPage.descPh')" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dlg = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitNew">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>
  </div>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import FormDialog from '@/components/FormDialog.vue'
import { getChangeOrderPage, createChangeOrder, approveChangeOrder, rejectChangeOrder } from '@/api/changeOrder'
import { getContractPage } from '@/api/contract'
import { enumSelectOptions, labelChangeOrderApproval, changeOrderCanApproveReject } from '@/utils/crmLabels'
import store from '@/store'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'

export default {
  name: 'ChangeOrderList',
  components: { ListPageShell, FormDialog },
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      dlg: false,
      saving: false,
      form: {
        contractId: null,
        amountDelta: '',
        descriptionText: ''
      },
      contractOptions: [],
      contractsLoading: false,
      filters: {
        contractId: null,
        changeNoLike: '',
        approvalStatusCode: null
      }
    }
  },
  computed: {
    filterContractId() {
      const q = this.$route.query.contractId
      const n = q != null && q !== '' ? Number(q) : NaN
      return Number.isNaN(n) ? null : n
    },
    approvalOptions() {
      void this.$locale
      return enumSelectOptions(this.$t, 'changeOrderApproval', [1, 2, 3, 4])
    },
    canOfficeAct() {
      const roles = (store.state.userInfo && store.state.userInfo.roleCodes) || []
      return roles.includes('OFFICE') || roles.includes('ADMIN')
    }
  },
  created() {
    this.syncContractFromRoute()
    this.load()
    this.loadContracts()
  },
  watch: {
    '$route.query.contractId'() {
      this.syncContractFromRoute()
      this.pageNo = 1
      this.load()
    }
  },
  methods: {
    formatDisplayDateTime,
    changeOrderCanApproveReject,
    syncContractFromRoute() {
      const id = this.filterContractId
      this.filters.contractId = id != null ? id : null
    },
    labelChangeOrderApproval,
    contractOptionLabel(c) {
      const fallback = this.$t('grid.contract')
      return `${c.contractNo || c.id} · ${c.contractTitle || fallback}`
    },
    handlePageChange(p) {
      this.pageNo = p
      this.load()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.load()
    },
    async loadContracts() {
      this.contractsLoading = true
      try {
        const r = await getContractPage({ pageNo: 1, pageSize: 500 })
        const d = r.data || r
        this.contractOptions = d.records || []
      } catch (e) {
        this.contractOptions = []
      } finally {
        this.contractsLoading = false
      }
    },
    handleSearch() {
      this.pageNo = 1
      this.load()
    },
    handleReset() {
      this.filters = { contractId: null, changeNoLike: '', approvalStatusCode: null }
      this.syncContractFromRoute()
      this.pageNo = 1
      this.load()
    },
    async load() {
      this.loading = true
      try {
        const params = { pageNo: this.pageNo, pageSize: this.pageSize }
        if (this.filters.contractId != null && this.filters.contractId !== '') {
          params.contractId = this.filters.contractId
        }
        const cn = (this.filters.changeNoLike || '').trim()
        if (cn) params.changeNoLike = cn
        if (this.filters.approvalStatusCode != null && this.filters.approvalStatusCode !== '') {
          params.approvalStatusCode = this.filters.approvalStatusCode
        }
        const r = await getChangeOrderPage(params)
        const d = r.data || r
        this.rows = d.records || []
        this.total = d.total || 0
      } finally {
        this.loading = false
      }
    },
    openNew() {
      this.dlg = true
      if (this.filters.contractId) {
        this.form.contractId = this.filters.contractId
      }
    },
    resetForm() {
      this.form = { contractId: this.filters.contractId || null, amountDelta: '', descriptionText: '' }
    },
    async submitNew() {
      if (!this.form.contractId) {
        this.$message.warning(this.$t('changeOrderPage.selectContract'))
        return
      }
      this.saving = true
      try {
        const amt = this.form.amountDelta === '' || this.form.amountDelta == null ? null : Number(this.form.amountDelta)
        await createChangeOrder({
          contractId: this.form.contractId,
          amountDelta: Number.isNaN(amt) ? null : amt,
          descriptionText: this.form.descriptionText || null
        })
        this.$message.success(this.$t('changeOrderPage.created'))
        this.dlg = false
        await this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.saving = false
      }
    },
    approve(row) {
      this.$confirm(this.$t('changeOrderPage.confirmApprove'), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          await approveChangeOrder(row.id)
          this.$message.success(this.$t('common.approved'))
          await this.load()
        })
        .catch(() => {})
    },
    reject(row) {
      this.$confirm(this.$t('changeOrderPage.confirmReject'), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          await rejectChangeOrder(row.id)
          this.$message.success(this.$t('common.rejected'))
          await this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.filter-hint {
  font-size: 12px;
  color: var(--color-text-sub);
  margin: 0 0 12px;
}

.danger {
  color: #f87171;
}

.list-pager {
  margin-top: 16px;
  text-align: right;
}
</style>
